import { useNavigate } from 'react-router-dom';
import { ClockIcon, PeopleIcon, ChecklistIcon } from '../activity-details/icons';
import LikeCountBadge from '../activity-details/LikeCountBadge';
import { createActivityNavigationHandler } from '../../utils/navigation';
import defaultActivityPlaceholder from '../../../assets/images/default-activity.jpeg';
import {
  cardWidth,
  cardPadding,
  cardGap,
  cardImageHeight,
  cardTitle,
  cardMetadata,
  cardDescription,
  ctaButtonHeight,
} from './constants';

export interface HorizontalCardActivity {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  duration: number;
  durationText?: string;
  playType: string;
  playTypeText?: string;
  prepNeeded: string;
  prepNeededText?: string;
  likesCount: number;
  whyItWorks?: { text: string; boldWords: string[] } | null;
  categoryColor?: string | null;
}

interface LibraryActivityHorizontalCardProps {
  activity: HorizontalCardActivity;
}

export default function LibraryActivityHorizontalCard({ activity }: LibraryActivityHorizontalCardProps) {
  const navigate = useNavigate();
  const handleClick = createActivityNavigationHandler(activity.id, navigate);

  return (
    <div
      onClick={handleClick}
      className={`flex-shrink-0 flex flex-col bg-white cursor-pointer ${cardWidth} ${cardPadding} ${cardGap}`}
      style={{
        borderRadius: '24px',
        boxShadow: '0px 4px 16px rgba(0, 124, 173, 0.2)',
      }}
    >
      {/* Image */}
      <div className={`relative overflow-hidden flex-shrink-0 ${cardImageHeight}`} style={{ borderRadius: '12px' }}>
        <img
          src={activity.imageUrl ?? defaultActivityPlaceholder}
          alt={activity.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {activity.likesCount > 0 && (
          <LikeCountBadge count={activity.likesCount} className="absolute bottom-2 right-2" />
        )}
      </div>

      {/* Title */}
      <h3 className={`line-clamp-1 ${cardTitle}`} style={{ fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
        {activity.title}
      </h3>

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <ClockIcon size={14} color="#323B46" />
            <span className={cardMetadata} style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {activity.durationText ?? `${activity.duration} min`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <PeopleIcon size={14} color="#323B46" />
            <span className={cardMetadata} style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {activity.playTypeText ?? activity.playType}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <ChecklistIcon size={14} color="#323B46" />
            <span className={cardMetadata} style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {activity.prepNeededText ?? activity.prepNeeded}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className={`line-clamp-2 ${cardDescription}`} style={{ fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
        {activity.description}
      </p>

      {/* Why it works chip */}
      {activity.whyItWorks && (
        <div style={{ backgroundColor: '#DBF7F8', borderRadius: '8px', padding: '8px 12px' }}>
          <p
            className={`line-clamp-2 text-[12px] text-[#007CAD]`}
            style={{ fontFamily: 'DM Sans, sans-serif', margin: 0 }}
          >
            {activity.whyItWorks.text}
          </p>
        </div>
      )}

      {/* CTA button */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClick(); }}
        className={`w-full ${ctaButtonHeight} rounded-[12px] border-none cursor-pointer`}
        style={{
          backgroundColor: activity.categoryColor || '#1B2831',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '12px',
          fontWeight: 900,
          color: '#FFFFFF',
          letterSpacing: '0.72px',
          textTransform: 'uppercase',
        }}
      >
        TRY THIS SPARK
      </button>
    </div>
  );
}
